import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { BookOpen, Search, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';

const CATEGORIES = ['전체', '덴탈아리랑', '치과신문', '치의신보', '덴탈아카이브'];
const SOURCE_COLORS = { '덴탈아리랑': '#2563eb', '치과신문': '#059669', '치의신보': '#7c3aed', '덴탈아카이브': '#dc2626' };

function Clinical({ user }) {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSource, setSelectedSource] = useState('전체');
    const [searchTerm, setSearchTerm] = useState('');
    const [expanded, setExpanded] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'clinicalContents', 'cache'));
                if (snap.exists()) {
                    const data = snap.data();
                    setArticles(data.articles || []);
                    setLastUpdated(data.lastUpdated);
                }
            } catch (e) { console.error('학술/임상 로드 실패:', e); }
            setLoading(false);
        };
        load();
    }, []);

    const filtered = articles.filter(a => {
        if (selectedSource !== '전체' && a.source !== selectedSource) return false;
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            return (a.title || '').toLowerCase().includes(s) || (a.description || '').toLowerCase().includes(s);
        }
        return true;
    }).sort((a, b) => {
        const da = a.pubDate ? new Date(a.pubDate).getTime() : 0;
        const db = b.pubDate ? new Date(b.pubDate).getTime() : 0;
        return db - da; // 최신순
    });

    const fmtDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const d = new Date(dateStr);
            return d.toLocaleDateString('ko-KR');
        } catch { return dateStr; }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>로딩 중...</div>;

    return (
        <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 24, fontWeight: 700, margin: '0 0 8px' }}>
                <BookOpen size={28} color="#0d9488" /> 학술/임상
            </h1>
            {lastUpdated && <p style={{ color: '#9ca3af', fontSize: 12, margin: '0 0 20px' }}>마지막 업데이트: {fmtDate(lastUpdated?.toDate?.() || lastUpdated)}</p>}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {CATEGORIES.map(c => (
                    <button key={c} onClick={() => setSelectedSource(c)}
                        style={{ padding: '6px 14px', border: '1px solid #e5e7eb', borderRadius: 20, background: selectedSource === c ? (SOURCE_COLORS[c] || '#0d9488') : '#fff', color: selectedSource === c ? '#fff' : '#374151', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                        {c}
                    </button>
                ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 20 }}>
                <Search size={16} color="#9ca3af" />
                <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="기사 검색" style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14 }} />
            </div>
            <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>총 {filtered.length}건</p>
            {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}><BookOpen size={48} color="#d1d5db" /><p>기사가 없습니다</p></div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {filtered.map((a, i) => (
                        <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                            <div onClick={() => setExpanded(p => ({ ...p, [i]: !p[i] }))} style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                        <span style={{ padding: '2px 8px', borderRadius: 4, background: (SOURCE_COLORS[a.source] || '#6b7280') + '15', color: SOURCE_COLORS[a.source] || '#6b7280', fontSize: 11, fontWeight: 600 }}>{a.source}</span>
                                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(a.pubDate)}</span>
                                    </div>
                                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>{a.title}</h3>
                                </div>
                                {expanded[i] ? <ChevronUp size={18} color="#9ca3af" /> : <ChevronDown size={18} color="#9ca3af" />}
                            </div>
                            {expanded[i] && (
                                <div style={{ padding: '0 16px 14px', borderTop: '1px solid #f3f4f6' }}>
                                    {a.description && <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, margin: '12px 0' }}>{a.description}</p>}
                                    {a.link && <a href={a.link} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#0d9488', fontSize: 13, fontWeight: 500 }}>원문 보기 <ExternalLink size={12} /></a>}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default Clinical;
